import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { Box, styled, useTheme } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AnimatePresence, motion } from 'framer-motion';
import { SButton, SRoundedContainer } from '@setvi/shared/components';
import { Customer } from 'Services/Mongo/Types';
import {
  CatalogConfig,
  PriceGroup,
  Product,
  ProductColumnsIds,
  ProductDetailsTabsIds
} from '@setvi/shared/interfaces';

import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';
import { InvalidConfiguration } from 'pages/products/components/invalid-configuration';
import { PricingType } from 'pages/products/api';

import { IntelligenceIcon } from '@setvi/shared/components/sicons';

import { usePdvFeedbackTab } from 'componentsV2/product/data-feedback/feedback-tab';
import { Footer, Header, ProductAskAI, Tabs } from './components';
import type {
  ProductDetailsExtraTab,
  ProductDetailsTabId
} from './components/tabs';
import {
  AssociatedProducts,
  AssociatedResource,
  OrderHistory,
  ProductInfo,
  SalesEnablement,
  SimilarProducts,
  Warehouse,
  ProductEnhancer
} from './subpages';
import { AskAiButton } from './components/ask-ai/styled';
import { useProductAskAi } from './components/ask-ai/hook/use-ask-ai';
import { useAskAIStorage } from './components/ask-ai/store';
import { ShipToOrderHistory } from './subpages/order-history/ship-to-order-history';

const Container = styled(SRoundedContainer)({
  width: '100%',
  overflow: 'hidden auto'
});

const AnimatedBox = motion(Box);

export interface DetailsContentProps {
  product: Product;
  selectedProducts?: Product[];
  selectedCustomer?: Customer;
  defaultOpenedTab?: ProductDetailsTabId;
  extraTabs?: ProductDetailsExtraTab[];
  headerActions?: ReactNode;
  hideFeedbackList?: boolean;
  canSelectProducts?: boolean;
  selectedCategoryId?: string;
  selectionType?: 'none' | 'checkbox' | 'radio';
  readOnly?: boolean;
  hideProductPrice?: boolean;
  configOverride?: CatalogConfig;
  orderHistoryValues?: {
    accountId: string;
    addressId?: string;
    infoMessage?: string;
    customerName?: string;
    showShipToButton?: boolean;
  };
  selectedPriceGroup?: PriceGroup;
  priceLoading?: boolean;
  pricingType?: PricingType;
  deliveryDate?: string;
  disableStockRefresh?: boolean;
  handleProductSelect?: (isChecked: boolean, product: Product[]) => void;
  updateProduct?: (product: Product, props: Partial<Product>) => void;
  captureResourceAnalytics?: (additionalValues?: {
    [key: string]: string;
  }) => void;
  openProductDetails?: (
    product: Product,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => void;
  closeDialog?: () => void;
  handleEmail?: () => void;
  handleSelected?: () => void;
  handleCloseActionBar?: () => void;
}

export const DetailsContent = ({
  product,
  selectedProducts,
  selectedCustomer,
  defaultOpenedTab = ProductDetailsTabsIds.PRODUCT_INFO,
  extraTabs,
  headerActions,
  hideFeedbackList,
  canSelectProducts,
  selectedCategoryId,
  selectionType,
  priceLoading,
  hideProductPrice = false,
  configOverride,
  readOnly = false,
  orderHistoryValues,
  selectedPriceGroup,
  pricingType,
  deliveryDate,
  disableStockRefresh,
  handleProductSelect,
  updateProduct,
  captureResourceAnalytics,
  openProductDetails,
  closeDialog,
  handleEmail,
  handleSelected,
  handleCloseActionBar
}: DetailsContentProps) => {
  const flags = useFlags();
  const { spacing } = useTheme();

  const { openAskAi, setValue, setOpenAskAi } = useAskAIStorage();
  const [activeTab, setActiveTab] =
    useState<ProductDetailsTabId>(defaultOpenedTab);
  const [showShipToHistory, setShowShipToHistory] = useState(false);

  const handleOpenShipTo = useCallback(() => setShowShipToHistory(true), []);
  const handleCloseShipTo = useCallback(() => setShowShipToHistory(false), []);

  const { configuration: baseConfiguration, invalidConfiguration } =
    useCatalogConfig();
  const configuration = configOverride || baseConfiguration;
  const isRestrictedView = !!configOverride;

  const hasColumn = (id: ProductColumnsIds) =>
    configOverride?.product?.columns?.some(c => c.id === id);

  const hideManufacturer =
    isRestrictedView && !hasColumn(ProductColumnsIds.Manufacturer);

  const effectiveHideProductPrice = isRestrictedView
    ? !hasColumn(ProductColumnsIds.Price) &&
      !hasColumn(ProductColumnsIds.ListPrice) &&
      !hasColumn(ProductColumnsIds.GetPrice)
    : hideProductPrice;

  const parentRef = useRef<HTMLDivElement>(null);
  const tabs = configuration.productDetails?.tabs || [];

  const tabStyle = useMemo(
    () => ({
      display: 'flex',
      overflow: 'hidden',
      padding: '24px',
      paddingTop: 8,
      flex: 1,
      paddingRight: openAskAi ? '12px' : '24px'
    }),
    [openAskAi]
  );

  const {
    questions,
    conversation,
    initLoading,
    loading,
    onSubmit,
    setConversation
  } = useProductAskAi({
    product
  });

  useEffect(() => {
    setActiveTab(defaultOpenedTab);
  }, [defaultOpenedTab]);

  // Product Data Verification adds a Feedback tab everywhere the dialog opens,
  // except customer-facing (restricted) views. A caller passing its own tab with
  // the same id (the admin Rep Feedback queue) replaces it.
  const pdvFeedbackTab = usePdvFeedbackTab(product?.sku);
  const ownTabs = extraTabs ?? [];
  const tabsWithFeedback =
    !isRestrictedView &&
    pdvFeedbackTab &&
    !ownTabs.some(t => t.id === pdvFeedbackTab.id)
      ? [...ownTabs, pdvFeedbackTab]
      : ownTabs;

  if (invalidConfiguration) return <InvalidConfiguration />;

  if (showShipToHistory && orderHistoryValues) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        minHeight={0}
        overflow="hidden auto"
        p={3}>
        <ShipToOrderHistory
          productId={product._id}
          accountId={orderHistoryValues.accountId}
          addressId={orderHistoryValues.addressId}
          customerName={orderHistoryValues.customerName}
          productName={product.name}
          onBack={handleCloseShipTo}
          onClose={closeDialog}
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        flex={1}
        minHeight={0}
        overflow="hidden auto">
        <Box px={4} py={3} display="flex" justifyContent="space-between">
          <Header
            product={product}
            selectedCustomer={selectedCustomer}
            selectedPriceGroup={selectedPriceGroup}
            hideProductPrice={effectiveHideProductPrice}
            hideManufacturer={hideManufacturer}
            priceLoading={priceLoading}
            pricing={configuration.productDetails?.pricing}
            priceType={configuration.price}
            pricingType={pricingType}
            deliveryDate={deliveryDate}
            productDetails={configuration.productDetails}
            closeDetailsDialog={closeDialog}
            actions={headerActions}
            updateProduct={updateProduct}
            captureResourceAnalytics={captureResourceAnalytics}
            stockRefresh={
              disableStockRefresh
                ? false
                : configuration.productDetails.stock.refresh
            }
            onStockClick={() => setActiveTab(ProductDetailsTabsIds.WAREHOUSE)}
          />
        </Box>

        <Box display="flex" flex={1} overflow="hidden auto">
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            minHeight={0}
            overflow="hidden auto"
            // @ts-ignore
            ref={parentRef}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              overflow="auto"
              pr={4}>
              <Tabs
                currentTab={activeTab}
                tabs={tabs}
                onTabChange={setActiveTab}
                orderHistoryValues={!!orderHistoryValues}
                isRestrictedView={isRestrictedView}
                extraTabs={tabsWithFeedback}
              />

              <Box
                display="flex"
                alignItems="center"
                gridGap={8}
                flexShrink={0}>
                {activeTab === ProductDetailsTabsIds.ORDER_HISTORY &&
                  orderHistoryValues?.addressId &&
                  orderHistoryValues?.showShipToButton && (
                    <SButton
                      variant="text"
                      color="primary"
                      onClick={handleOpenShipTo}
                      style={{
                        textTransform: 'none',
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}>
                      Ship To Order History
                    </SButton>
                  )}
                {!isRestrictedView && !openAskAi && flags?.productAssistant && (
                  <AskAiButton>
                    <SButton
                      variant="outlined"
                      onClick={() => setOpenAskAi(true)}
                      startIcon={<IntelligenceIcon />}>
                      Ask AI
                    </SButton>
                  </AskAiButton>
                )}
              </Box>
            </Box>

            {activeTab === ProductDetailsTabsIds.PRODUCT_INFO && (
              <Box style={tabStyle}>
                <ProductInfo
                  product={product}
                  hideFeedbackList={hideFeedbackList}
                />
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.WAREHOUSE && (
              <Box style={tabStyle}>
                <Container>
                  <Warehouse
                    product={product}
                    selectedCustomer={selectedCustomer}
                    configColumns={
                      configuration.productDetails.tabs.find(
                        i => i.id === ProductDetailsTabsIds.WAREHOUSE
                      )?.columns
                    }
                  />
                </Container>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.ASSOCIATED_RESOURCE && (
              <Box style={tabStyle}>
                <Container display="flex">
                  <AssociatedResource
                    product={product}
                    readOnly={readOnly}
                    captureResourceAnalytics={captureResourceAnalytics}
                    closeDialog={closeDialog}
                  />
                </Container>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.ASSOCIATED_PRODUCTS && (
              <Box style={tabStyle}>
                <Container display="flex">
                  <AssociatedProducts
                    product={product}
                    selectedProducts={selectedProducts}
                    canSelectProducts={canSelectProducts}
                    readOnly={readOnly}
                    handleProductSelect={handleProductSelect}
                    openProductDetails={openProductDetails}
                    closeDialog={closeDialog}
                    handleEmail={handleEmail}
                    handleSelected={handleSelected}
                    handleCloseActionBar={handleCloseActionBar}
                  />
                </Container>
              </Box>
            )}
            {activeTab === ProductDetailsTabsIds.SIMILAR_PRODUCTS && (
              <Box style={tabStyle}>
                <Container display="flex">
                  <SimilarProducts
                    product={product}
                    selectedProducts={selectedProducts}
                    canSelectProducts={canSelectProducts}
                    scrollParentRef={parentRef}
                    closeDialog={closeDialog}
                    readOnly={readOnly}
                    configOverride={configOverride}
                    openProductDetails={val => {
                      setActiveTab(ProductDetailsTabsIds.PRODUCT_INFO);
                      openProductDetails(val);
                    }}
                    handleProductSelect={handleProductSelect}
                    selectedCustomer={selectedCustomer}
                    handleEmail={handleEmail}
                    handleSelected={handleSelected}
                    handleCloseActionBar={handleCloseActionBar}
                  />
                </Container>
              </Box>
            )}
            {!isRestrictedView &&
              activeTab === ProductDetailsTabsIds.SALES_ENABLEMENT &&
              flags?.salesEnablement && (
                <Box style={tabStyle}>
                  <SalesEnablement product={product} />
                </Box>
              )}

            {activeTab === ProductDetailsTabsIds.ORDER_HISTORY && (
              <Box style={tabStyle}>
                <Container>
                  <OrderHistory
                    productId={product._id}
                    {...orderHistoryValues}
                  />
                </Container>
              </Box>
            )}

            {tabsWithFeedback
              .filter(t => t.id === activeTab)
              .map(t => (
                <Box key={t.id} style={tabStyle}>
                  {t.content}
                </Box>
              ))}

            {!isRestrictedView &&
              activeTab === ProductDetailsTabsIds.PRODUCT_ENHANCER &&
              flags?.productEnhancer && (
                <Box style={tabStyle}>
                  <ProductEnhancer product={product} />
                </Box>
              )}
          </Box>

          <AnimatePresence>
            {!isRestrictedView && flags?.productAssistant && openAskAi && (
              <AnimatedBox
                key="ask-ai-box"
                initial={{ width: 0, opacity: 0, padding: 0 }}
                animate={{
                  width: 350,
                  opacity: 1,
                  padding: spacing(2, 4, 4, 0)
                }}
                exit={{ width: 0, opacity: 0, padding: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                style={{
                  overflow: 'hidden'
                }}>
                <ProductAskAI
                  onClose={() => {
                    setValue('');
                    setOpenAskAi(false);
                  }}
                  onSubmit={onSubmit}
                  conversation={conversation}
                  loading={loading}
                  product={product}
                  initLoading={initLoading}
                  questions={questions}
                  setConversation={setConversation}
                />
              </AnimatedBox>
            )}
          </AnimatePresence>
        </Box>

        <Footer
          product={product}
          readOnly={readOnly}
          selectedProducts={selectedProducts}
          selectedCategoryId={selectedCategoryId}
          handleProductSelect={handleProductSelect}
          canSelectProducts={canSelectProducts}
          selectionType={selectionType}
          configOverride={configOverride}
        />
      </Box>
    </>
  );
};
